<template>
  <div>
    <svg :viewBox="`0 0 ${M.w} ${M.h}`" style="max-width:720px;margin:0 auto">
      <g v-for="r in shapes" :key="r.ar" v-tip="r.tip" class="map-tile">
        <path :d="r.d" :fill="r.fill" stroke="var(--sd-card)" stroke-width="1.4" stroke-linejoin="round" />
        <template v-if="r.ext">
          <line :x1="r.ext.lx" :y1="r.ext.ly" :x2="r.ext.tx + 6" :y2="r.ext.ty - 4" stroke="var(--sd-ink-4)" stroke-width="1" />
          <text :x="r.ext.tx" :y="r.ext.ty - 6" text-anchor="end" class="map-name" fill="var(--sd-ink-2)">{{ r.name }}</text>
          <text :x="r.ext.tx" :y="r.ext.ty + 9" text-anchor="end" class="map-val" fill="var(--sd-ink)" font-size="12">{{ r.val }}</text>
        </template>
        <template v-else>
          <text :x="r.lx" :y="r.ly - 4" text-anchor="middle" class="map-name" :fill="r.light ? '#fff' : 'var(--sd-ink-2)'">{{ r.name }}</text>
          <text :x="r.lx" :y="r.ly + 13" text-anchor="middle" class="map-val" :fill="r.light ? '#fff' : 'var(--sd-ink)'">{{ r.val }}</text>
        </template>
      </g>
    </svg>
    <div class="heatleg">
      <template v-if="metric === 'part'">
        <span>{{ t('m_part') }}: {{ t('m_low') }}</span>
        <span class="ramp" :style="{background: `linear-gradient(${isAr ? 'to left' : 'to right'},${partRamp(0)},${partRamp(1)})`}" />
        <span>{{ t('m_high') }}</span>
      </template>
      <template v-else>
        <span>{{ t('m_sat') }}: <span class="num-ltr">25%</span></span>
        <span class="ramp" :style="{background: `linear-gradient(${isAr ? 'to left' : 'to right'},#c9403c,#d9a81f,#0c6e4b)`}" />
        <span class="num-ltr">60%</span>
      </template>
    </div>
  </div>
</template>
<script>
/* Real 13-region Saudi map (geoBoundaries ADM1 geometry), coloured by participation or satisfaction.
   stats: { [regionAr]: { participants, rated, satPct } } */
import { SA_MAP } from '../../lib/saMap.js'
import { mix, compact, fmt, pctS } from '../../lib/format.js'
import { chartPalette } from '../../theme.js'
import { sdMixin } from '../../store.js'
const EXT = { 'جازان': { tx: 212, ty: 590, lx: 282, ly: 578 }, 'الباحة': { tx: 132, ty: 452, lx: 243, ly: 470 } }
const OFF = { 'الحدود الشمالية': { dx: 48, dy: -10 }, 'الجوف': { dx: -14, dy: 12 }, 'تبوك': { dx: -6, dy: 26 } }
export default {
  name: 'SdSaudiMap',
  mixins: [sdMixin],
  props: { stats: { type: Object, default: () => ({}) }, metric: { type: String, default: 'part' } },
  computed: {
    M() { return SA_MAP },
    pal() { return chartPalette[this.dark ? 'dark' : 'light'] },
    maxPart() { return Math.max(1, ...Object.values(this.stats).map((s) => s.participants || 0)) },
    shapes() {
      return this.M.regions.map((r) => {
        const st = this.stats[r.ar] || { participants: 0, rated: 0, satPct: null }
        const f = Math.sqrt((st.participants || 0) / this.maxPart)
        const fill = this.metric === 'part' ? this.partRamp(f) : this.satColor(st.satPct)
        const light = this.metric === 'part' ? f > 0.55 : (st.satPct != null && (st.satPct < 35 || st.satPct > 50))
        const name = this.isAr ? r.ar : r.en; const off = OFF[r.ar] || { dx: 0, dy: 0 }
        return { ar: r.ar, d: r.d, fill, light, name, ext: EXT[r.ar], lx: r.cx + off.dx, ly: r.cy + off.dy,
          val: this.metric === 'part' ? compact(st.participants) : (st.satPct != null ? pctS(st.satPct) : '—'),
          tip: `<b>${name}</b><br><span class="tt-k">${this.t('m_part')}:</span> ${fmt(st.participants)}<br><span class="tt-k">${this.t('rated_u')}:</span> ${fmt(st.rated)}<br><span class="tt-k">${this.t('m_sat')}:</span> ${st.satPct != null ? pctS(st.satPct) : '—'}` }
      })
    },
  },
  methods: {
    partRamp(f) { return mix(this.pal.heat0, this.pal.green600, 0.12 + 0.88 * f) },
    satColor(p) { if (p == null) return this.pal.line; const f = Math.max(0, Math.min(1, (p - 25) / 35)); return f < 0.5 ? mix('#c9403c', '#d9a81f', f * 2) : mix('#d9a81f', '#0c6e4b', (f - 0.5) * 2) },
  },
}
</script>
